import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { normalizeApiError } from "./utils/errorUtils";

// ─── Context Providers ────────────────────────────────────────────────────────
import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider } from "./context/AuthContext";

// ─── Layouts ──────────────────────────────────────────────────────────────────
import { AuthLayout } from "./layouts/AuthLayout";
import { DashboardLayout } from "./layouts/DashboardLayout";
import { PublicLayout } from "./layouts/PublicLayout";

// ─── Auth Guards ──────────────────────────────────────────────────────────────
import { ProtectedRoute } from "./components/auth/ProtectedRoute";

// ─── Shared (must be synchronously available) ─────────────────────────────────
import { PageLoader } from "./components/public/PageLoader";
import { SessionTimeoutModal } from "./components/auth/SessionTimeoutModal";
import { ChunkErrorBoundary } from "./components/auth/ChunkErrorBoundary";

// ─── Route groups ─────────────────────────────────────────────────────────────
import { renderPublicRoutes } from "./router/public.routes";
import { renderAuthRoutes } from "./router/auth.routes";
import { renderDashboardRoutes } from "./router/dashboard.routes";
import { renderAdminRoutes } from "./router/admin.routes";
import { renderErpRoutes } from "./router/erp.routes";

// ─── React Query client ───────────────────────────────────────────────────────
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// ─── Root Error Boundary ──────────────────────────────────────────────────────
interface RootErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class RootErrorBoundary extends React.Component<
  { children: React.ReactNode },
  RootErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): RootErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("[RootErrorBoundary] Caught React runtime exception:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col items-center justify-center p-6 text-center font-sans">
          <div className="max-w-md w-full bg-slate-900 border border-red-500/40 rounded-3xl p-8 shadow-2xl space-y-4">
            <div className="w-14 h-14 rounded-2xl bg-red-950/80 border border-red-500/40 flex items-center justify-center mx-auto text-red-400 font-bold text-2xl">
              !
            </div>
            <h1 className="text-xl font-extrabold text-white">Application Runtime Recovered</h1>
            <p className="text-xs text-slate-400 leading-relaxed">
              An unhandled component exception was caught by the root Error Boundary.
            </p>
            <pre className="text-[11px] font-mono bg-slate-950 p-3 rounded-xl border border-slate-800 text-red-300 text-left overflow-x-auto max-h-32">
              {normalizeApiError(this.state.error)}
            </pre>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.href = "/";
              }}
              className="w-full py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs rounded-xl shadow-lg shadow-indigo-600/30 transition-all"
            >
              Reload Application
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── Root App Component ───────────────────────────────────────────────────────
export const App: React.FC = () => {
  return (
    <RootErrorBoundary>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <BrowserRouter>
              <SessionTimeoutModal />
              <ChunkErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    {/* Public Marketing Suite */}
                    {renderPublicRoutes()}

                    {/* Multi-Portal Authentication */}
                    {renderAuthRoutes()}

                    {/* Protected ERP Dashboard */}
                    <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
                      {renderDashboardRoutes()}
                      {renderAdminRoutes()}
                      {renderErpRoutes()}
                    </Route>
                  </Routes>
                </Suspense>
              </ChunkErrorBoundary>
            </BrowserRouter>
          </AuthProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </RootErrorBoundary>
  );
};

export default App;
